"use client";
import React, { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useAuth } from '@/contexts/AuthContext';
import { memberService, canManageMembers, canRecordAttendance, canAddMembers, type Member } from '@/lib/supabase-service';

const PIPELINE_STAGE_LABELS: Record<Member['pipelineStage'], string> = {
  visitor: 'First Visit',
  membership_class: 'Membership Class',
  full_member: 'Full Member',
};

const STATUS_COLORS: Record<Member['status'], string> = {
  Active: 'text-green-400 border-green-400/40 bg-green-400/10',
  Inactive: 'text-silver-dim border-border-strong bg-transparent',
  New: 'text-gold border-gold/40 bg-gold/10',
};

const DEPARTMENTS_LIST = ['', 'Choir', 'Deacons', 'Youth Ministry', 'Prayer Team', 'Ushers', 'Media Team', 'Hospitality', 'Missions'];

interface Toast { type: 'success' | 'error' | 'info'; message: string; }

export default function MembersPage() {
  const router = useRouter();
  const { user, profile, loading: authLoading } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'All' | Member['status']>('All');
  const [showForm, setShowForm] = useState(false);
  const [editMember, setEditMember] = useState<Member | null>(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', status: 'Active' as Member['status'], notes: '', nfcTagId: '', department: '', pipelineStage: 'visitor' as Member['pipelineStage'] });
  const [nfcScanning, setNfcScanning] = useState(false);
  const [nfcSupported, setNfcSupported] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [assignNfcMode, setAssignNfcMode] = useState(false);
  const [assignTarget, setAssignTarget] = useState<Member | null>(null);
  const nfcReaderRef = useRef<NDEFReader | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/login');
      return;
    }
    if (!authLoading && user) {
      loadMembers();
      if (typeof window !== 'undefined' && 'NDEFReader' in window) setNfcSupported(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user]);

  useEffect(() => {
    if (!user) return;
    const unsubscribe = memberService.subscribeToChanges((updatedMembers) => {
      setMembers(updatedMembers);
    });
    return unsubscribe;
  }, [user]);

  async function loadMembers() {
    setDataLoading(true);
    try {
      const data = await memberService.getAll();
      setMembers(data);
    } catch (err: any) {
      showToast('error', err?.message || 'Failed to load members.');
    } finally {
      setDataLoading(false);
    }
  }

  const canEdit = profile ? canManageMembers(profile.role) : false;
  const canAttend = profile ? canRecordAttendance(profile.role) : false;
  const canAdd = profile ? canAddMembers(profile.role) : false;

  function showToast(type: Toast['type'], message: string) {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ type, message });
    toastTimerRef.current = setTimeout(() => setToast(null), 4000);
  }

  async function recordAttendanceManual(member: Member) {
    if (!profile) return;
    const recorded = await memberService.recordAttendance(member.id, member.name, profile.fullName);
    if (recorded) {
      const updated = await memberService.getAll();
      setMembers(updated);
      showToast('success', `✓ Attendance recorded for ${member.name}`);
    } else {
      showToast('info', `${member.name} already checked in today.`);
    }
  }

  async function startNfcAttendance() {
    if (!nfcSupported) { showToast('error', 'NFC requires Chrome on Android.'); return; }
    try {
      const ndef = new (window as Window & { NDEFReader: new () => NDEFReader }).NDEFReader();
      nfcReaderRef.current = ndef;
      await ndef.scan();
      setNfcScanning(true);
      showToast('info', 'NFC active — tap a member\'s card.');
      ndef.addEventListener('reading', async (event: NDEFReadingEvent) => {
        const tagId = event.serialNumber;
        const matched = members.find(m => m.nfcTagId === tagId);
        if (matched && profile) {
          await memberService.recordAttendance(matched.id, matched.name, profile.fullName);
          const updated = await memberService.getAll();
          setMembers(updated);
          showToast('success', `✓ Attendance recorded for ${matched.name}`);
        } else {
          showToast('error', `NFC tag not linked to any member.`);
        }
      });
      ndef.addEventListener('readingerror', () => showToast('error', 'NFC read error.'));
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setNfcScanning(false);
      showToast('error', `NFC error: ${msg}`);
    }
  }

  function stopNfcScan() {
    nfcReaderRef.current = null;
    setNfcScanning(false);
    setAssignNfcMode(false);
    setAssignTarget(null);
    showToast('info', 'NFC scanning stopped.');
  }

  async function startAssignNfc(member: Member) {
    if (!nfcSupported) { showToast('error', 'NFC requires Chrome on Android.'); return; }
    setAssignTarget(member);
    setAssignNfcMode(true);
    try {
      const ndef = new (window as Window & { NDEFReader: new () => NDEFReader }).NDEFReader();
      nfcReaderRef.current = ndef;
      await ndef.scan();
      showToast('info', `Tap NFC card to assign to ${member.name}`);
      ndef.addEventListener('reading', async (event: NDEFReadingEvent) => {
        const tagId = event.serialNumber;
        await memberService.update(member.id, { nfcTagId: tagId });
        const updated = await memberService.getAll();
        setMembers(updated);
        showToast('success', `NFC card assigned to ${member.name}`);
        nfcReaderRef.current = null;
        setAssignNfcMode(false);
        setAssignTarget(null);
        setNfcScanning(false);
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setAssignNfcMode(false);
      setAssignTarget(null);
      showToast('error', `NFC error: ${msg}`);
    }
  }

  const filtered = members.filter(m => {
    const matchSearch = m.name.toLowerCase().includes(search.toLowerCase()) || m.email.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'All' || m.status === filterStatus;
    return matchSearch && matchStatus;
  });

  function openAdd() {
    setEditMember(null);
    setForm({ name: '', phone: '', email: '', status: 'Active', notes: '', nfcTagId: '', department: '', pipelineStage: 'visitor' });
    setShowForm(true);
  }

  function openEdit(m: Member) {
    setEditMember(m);
    setForm({ name: m.name, phone: m.phone, email: m.email, status: m.status, notes: m.notes, nfcTagId: m.nfcTagId || '', department: m.department || '', pipelineStage: m.pipelineStage });
    setShowForm(true);
  }

  async function handleSave() {
    if (!form.name.trim()) return;
    const today = new Date().toISOString().split('T')[0];
    if (editMember) {
      await memberService.update(editMember.id, { ...form, nfcTagId: form.nfcTagId || undefined });
    } else {
      await memberService.create({ ...form, nfcTagId: form.nfcTagId || undefined, joinDate: today, lastVisit: today, attendanceDates: [today] });
    }
    const updated = await memberService.getAll();
    setMembers(updated);
    setShowForm(false);
  }

  async function handleDelete(id: string) {
    const success = await memberService.delete(id);
    if (success) {
      setMembers(prev => prev.filter(m => m.id !== id));
      showToast('success', 'Member removed successfully.');
    } else {
      showToast('error', 'Failed to remove member. You may not have permission.');
    }
  }

  const toastColors: Record<Toast['type'], string> = {
    success: 'bg-green-400/10 border-green-400/40 text-green-400',
    error: 'bg-red-400/10 border-red-400/40 text-red-400',
    info: 'bg-gold/10 border-gold/40 text-gold',
  };

  if (authLoading) return (
    <div className="min-h-screen bg-sanctuary flex items-center justify-center">
      <div className="noise-overlay" aria-hidden="true" />
      <p className="font-mono text-sm text-silver-dim relative z-10">Loading…</p>
    </div>
  );

  if (!user) return null;

  return (
    <div className="min-h-screen bg-sanctuary">
      <div className="noise-overlay" aria-hidden="true" />
      <Header />

      {toast && (
        <div className={`fixed top-6 right-6 z-[100] border px-5 py-3 font-mono text-xs tracking-wide max-w-sm shadow-lg ${toastColors[toast.type]}`}>
          {toast.message}
        </div>
      )}

      {nfcScanning && !assignNfcMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-sanctuary/90 backdrop-blur-sm">
          <div className="border border-gold/40 bg-vestment p-10 flex flex-col items-center gap-6 max-w-sm w-full mx-4">
            <div className="relative flex items-center justify-center">
              <span className="absolute inline-flex h-24 w-24 rounded-full bg-gold/10 animate-ping" />
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-gold relative z-10">
                <path d="M20 7a9 9 0 0 1 0 10M17 9.5a5 5 0 0 1 0 5M4 4l16 16M7 9.5a5 5 0 0 0 0 5M4 7a9 9 0 0 0 0 10" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-2">NFC Active</p>
              <p className="text-silver-dim text-sm font-light">Tap a member&apos;s NFC card to record attendance</p>
            </div>
            <button onClick={stopNfcScan} className="btn-ghost text-[11px] px-8 py-3 font-mono tracking-widest w-full">Stop Scanning</button>
          </div>
        </div>
      )}

      {assignNfcMode && assignTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-sanctuary/90 backdrop-blur-sm">
          <div className="border border-border-strong bg-vestment p-10 flex flex-col items-center gap-6 max-w-sm w-full mx-4">
            <div className="relative flex items-center justify-center">
              <span className="absolute inline-flex h-24 w-24 rounded-full bg-gold/10 animate-ping" />
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-gold relative z-10">
                <path d="M20 7a9 9 0 0 1 0 10M17 9.5a5 5 0 0 1 0 5M4 4l16 16M7 9.5a5 5 0 0 0 0 5M4 7a9 9 0 0 0 0 10" />
              </svg>
            </div>
            <div className="text-center">
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-2">Assign NFC Card</p>
              <p className="text-silver text-sm font-medium mb-1">{assignTarget.name}</p>
              <p className="text-silver-dim text-sm font-light">Tap the NFC card to link it</p>
            </div>
            <button onClick={stopNfcScan} className="btn-ghost text-[11px] px-8 py-3 font-mono tracking-widest w-full">Cancel</button>
          </div>
        </div>
      )}

      <main className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
          <div>
            <span className="section-label block mb-2">Compassion Check-in // Member Tracking</span>
            <h1 className="text-silver font-black text-3xl tracking-tight uppercase">Members</h1>
            <p className="text-silver-dim text-sm mt-1 font-light">Track, manage, and care for every member of your congregation.</p>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto flex-wrap">
            <button
              onClick={nfcScanning ? stopNfcScan : startNfcAttendance}
              className={`text-[11px] px-5 py-3 font-mono tracking-widest inline-flex items-center gap-2 border transition-colors ${nfcScanning ? 'bg-gold/10 border-gold text-gold hover:bg-gold/20' : nfcSupported ? 'bg-transparent border-border-strong text-silver-dim hover:border-gold hover:text-gold' : 'bg-transparent border-border-strong text-silver-dim/40 cursor-not-allowed'}`}
              title={!nfcSupported ? 'NFC requires Chrome on Android' : undefined}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M20 7a9 9 0 0 1 0 10M17 9.5a5 5 0 0 1 0 5M4 4l16 16M7 9.5a5 5 0 0 0 0 5M4 7a9 9 0 0 0 0 10" />
              </svg>
              {nfcScanning ? 'Stop NFC' : 'NFC Check-in'}
            </button>
            {canAdd && (
              <button onClick={openAdd} className="btn-gold text-[11px] px-6 py-3 font-mono tracking-widest inline-flex items-center gap-2">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 5v14M5 12h14" /></svg>
                Add Member
              </button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-px bg-border-strong border border-border-strong mb-8">
          {[
            { label: 'Total', value: members.length },
            { label: 'Active', value: members.filter(m => m.status === 'Active').length },
            { label: 'New', value: members.filter(m => m.status === 'New').length },
            { label: 'Inactive', value: members.filter(m => m.status === 'Inactive').length },
          ].map(stat => (
            <div key={stat.label} className="bento-cell px-6 py-5">
              <div className="stat-number text-3xl">{stat.value}</div>
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-silver-dim opacity-60 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <input
            type="text"
            placeholder="Search by name or email…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors flex-1"
          />
          <div className="flex gap-2">
            {(['All', 'Active', 'Inactive', 'New'] as const).map(s => (
              <button key={s} onClick={() => setFilterStatus(s)} className={`font-mono text-[10px] tracking-wider uppercase px-4 py-2 border transition-colors ${filterStatus === s ? 'border-gold text-gold' : 'border-border-strong text-silver-dim hover:border-gold hover:text-gold'}`}>{s}</button>
            ))}
          </div>
        </div>

        {/* Member table */}
        <div className="border border-border-strong overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-strong">
                {['Name', 'Contact', 'Status', 'Pipeline', 'Department', 'Last Visit', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-silver-dim opacity-60">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {dataLoading && (
                <tr><td colSpan={7} className="px-4 py-12 text-center font-mono text-sm text-silver-dim opacity-50">Loading members…</td></tr>
              )}
              {!dataLoading && filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center font-mono text-sm text-silver-dim opacity-50">No members found.</td></tr>
              )}
              {!dataLoading && filtered.map(m => (
                <tr key={m.id} className="border-b border-border-subtle hover:bg-vestment/50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="text-silver font-medium text-sm">{m.name}</div>
                    {m.nfcTagId && <span className="font-mono text-[9px] tracking-wider text-green-400 opacity-70">NFC Linked</span>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-silver-dim">{m.phone}</div>
                    <div className="font-mono text-xs text-silver-dim opacity-60">{m.email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border ${STATUS_COLORS[m.status]}`}>{m.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] text-silver-dim">{PIPELINE_STAGE_LABELS[m.pipelineStage]}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-[10px] text-silver-dim">{m.department || '—'}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-silver-dim">{m.lastVisit}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {canAttend && (
                        <button onClick={() => recordAttendanceManual(m)} className="font-mono text-[9px] tracking-wider uppercase px-3 py-1.5 border border-green-400/40 text-green-400 hover:bg-green-400/10 transition-colors">Check In</button>
                      )}
                      {canEdit && (
                        <>
                          <button onClick={() => startAssignNfc(m)} className="font-mono text-[9px] tracking-wider uppercase px-3 py-1.5 border border-border-strong text-silver-dim hover:border-gold hover:text-gold transition-colors">Assign NFC</button>
                          <button onClick={() => openEdit(m)} className="font-mono text-[9px] tracking-wider uppercase px-3 py-1.5 border border-border-strong text-silver-dim hover:border-gold hover:text-gold transition-colors">Edit</button>
                          <button onClick={() => handleDelete(m.id)} className="font-mono text-[9px] tracking-wider uppercase text-silver-dim hover:text-red-400 transition-colors">Remove</button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
      <Footer />

      {/* Add/Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-sanctuary/80 backdrop-blur-sm px-4 py-8 overflow-y-auto">
          <div className="bg-vestment border border-border-strong w-full max-w-lg p-8 my-auto">
            <h2 className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{editMember ? 'Edit Member' : 'Add Member'}</h2>
            <div className="space-y-4">
              {[
                { label: 'Full Name *', key: 'name', type: 'text', placeholder: 'Member full name' },
                { label: 'Phone', key: 'phone', type: 'tel', placeholder: '(555) 000-0000' },
                { label: 'Email', key: 'email', type: 'email', placeholder: 'member@email.com' },
                { label: 'NFC Tag ID', key: 'nfcTagId', type: 'text', placeholder: 'Optional — or use NFC scan' },
              ].map(({ label, key, type, placeholder }) => (
                <div key={key}>
                  <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">{label}</label>
                  <input type={type} placeholder={placeholder} value={(form as Record<string, string>)[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors" />
                </div>
              ))}
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Status</label>
                <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value as Member['status'] }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 focus:outline-none focus:border-gold transition-colors">
                  {(['Active', 'Inactive', 'New'] as const).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Pipeline Stage</label>
                <select value={form.pipelineStage} onChange={e => setForm(f => ({ ...f, pipelineStage: e.target.value as Member['pipelineStage'] }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 focus:outline-none focus:border-gold transition-colors">
                  <option value="visitor">First Visit</option>
                  <option value="membership_class">Membership Class</option>
                  <option value="full_member">Full Member</option>
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Department</label>
                <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 focus:outline-none focus:border-gold transition-colors">
                  {DEPARTMENTS_LIST.map(d => <option key={d} value={d}>{d || 'No Department'}</option>)}
                </select>
              </div>
              <div>
                <label className="font-mono text-[10px] tracking-widest uppercase text-silver-dim block mb-1.5">Care Notes</label>
                <textarea placeholder="Notes about this member…" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="w-full bg-sanctuary border border-border-strong text-silver font-mono text-sm px-4 py-2.5 placeholder:text-silver-dim/40 focus:outline-none focus:border-gold transition-colors resize-none" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleSave} className="btn-gold text-[11px] px-6 py-3 font-mono tracking-widest flex-1">Save Member</button>
              <button onClick={() => setShowForm(false)} className="btn-ghost text-[11px] px-6 py-3 font-mono tracking-widest">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
